import { useCallback, useRef, useState, useEffect } from "react";
import axios from "axios";
import { useAuth } from "../contexts/AuthContext";
import { useOneTimeHint } from "../hooks/useOneTimeHint";
import {
  VideoCamera,
  Brain,
  ClipboardText,
  ChatCircle,
  Warning,
  Clock,
  CheckCircle,
  XCircle,
  SpinnerGap,
  ArrowRight,
  ArrowClockwise,
  Trophy,
  Star,
  Exam,
  Lightning,
  Heart,
  Trash,
  Play,
  Sparkle,
  TrendUp,
  Gauge,
  Target,
  Lightbulb,
  Rocket,
  DownloadSimple
} from "@phosphor-icons/react";
import { Link, useNavigate } from "react-router-dom";
import { Button } from "../components/ui/button";
import { KNORIA_LOGO_URL } from "../components/KnoriaLogo";
import { useLang } from "../contexts/LanguageContext";
import { canEditContent, canAccessAdmin } from "../utils/permissions";
// Vincent B995 (Eric 2026-02-26 SPEC STRICK PROD) — Nuage de tags pondéré
// pour concepteurs, affiché sous le hero banner. Aperçu visuel + accès
// rapide à /dashboard/tags depuis la home.
import TagCloud from "../components/TagCloud";

import { API_BASE as API } from "../lib/apiBase";
import { libelleSource } from "../lib/sourceTranscription";

export default function DashboardPage() {
  const { user } = useAuth();
  const nav = useNavigate();
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [recentVideos, setRecentVideos] = useState([]);
  const [reprocessing, setReprocessing] = useState(null);
  const [progress, setProgress] = useState(null);
  const [favorites, setFavorites] = useState([]);
  // Eric 2026-07-28 (iter168) — IDs de favoris fraîchement ajoutés dans la
  // session courante. Sert à afficher un ring teal pulsant 2 s (B589 UX)
  // pour confirmer visuellement la synchro instantanée.
  const [pulsingFavIds, setPulsingFavIds] = useState(new Set());
  const [recommendations, setRecommendations] = useState(null);
  const [knowledgeGaps, setKnowledgeGaps] = useState(null);
  const [coachTips, setCoachTips] = useState(null);
  const [usage, setUsage] = useState(null);
  // Vincent B788 (Eric 2026-02-18 SPEC STRICK PROD) — Widget « Vidéos
  // suspectes » : compte les vidéos dont Whisper a produit < 20 segments
  // pour > 5 min de vidéo (heuristique aggressive_dedup / MP3 tronqué).
  const [suspectCount, setSuspectCount] = useState(null); // {count, top_examples}
  const { t, lang, tr } = useLang();

  // Vincent XLS iter103 (items 24, 34) → Eric 2026-06-25 (iter117 B2) — migré
  // vers canEditContent (whitelist canonique éditeurs, alignée backend
  // require_editor). Inclut désormais aussi employee_advanced + access_level=avance.
  const canUploadVideos = canEditContent(user);
  // Eric 2026-06-25 (iter118 B3) — migré vers canAccessAdmin pour les sections
  // « Admin Stats », « Relancer pipeline » et « Issues récentes ».
  const isAdmin = canAccessAdmin(user);

  // B1091 (Eric 2026-09-05) — UN SEUL APPEL. Avant : 11 appels par affichage
  // (dont `/api/videos` qui rapatriait TOUT le catalogue pour n'afficher que
  // 5 lignes, et `generate-alerts` envoyé deux fois parce que cet effet se
  // relançait quand le rôle de l'utilisateur arrivait). Le serveur agrège
  // désormais tous les blocs et décide lui-même de ceux réservés aux admins
  // et aux éditeurs — d'où des dépendances vides ici : un seul chargement.
  const alertsRequested = useRef(false);

  const loadData = useCallback(async () => {
    try {
      const { data } = await axios.get(`${API}/api/dashboard/bootstrap`, { withCredentials: true });
      // Le backend peut renvoyer null pour un bloc en panne : le reste de la
      // page doit s'afficher quand même (aucun widget ne blanchit l'écran).
      const asArray = (v) => Array.isArray(v) ? v : (Array.isArray(v?.items) ? v.items : []);
      setRecentVideos(asArray(data.recent_videos));
      if (data.progress) setProgress(data.progress);
      setFavorites(asArray(data.favorites));
      if (data.recommendations) setRecommendations(data.recommendations);
      if (data.knowledge_gaps) setKnowledgeGaps(data.knowledge_gaps);
      setCoachTips(Array.isArray(data.coach?.tips) ? data.coach.tips : []);
      if (data.admin_stats) setStats(data.admin_stats);
      if (data.usage) setUsage(data.usage);
      if (data.suspect_count) setSuspectCount(data.suspect_count);
    } catch (err) {
      console.error("Failed to load dashboard:", err);
    } finally {
      setLoading(false);
    }
    // Génération des alertes en arrière-plan — une seule fois par montage.
    if (!alertsRequested.current) {
      alertsRequested.current = true;
      axios.post(`${API}/api/ai/generate-alerts`, {}, { withCredentials: true }).catch(() => {});
    }
  }, []);

  useEffect(() => { loadData(); }, [loadData]);

  // Vincent B589 (Eric 2026-07-27 20h) — Rafraîchit la liste des favoris
  // instantanément quand on revient au Dashboard après un ajout ailleurs
  // (base de connaissances, explorateur de fichiers, dictionnaire…). Trois
  // déclencheurs :
  //   1. `focus` de la fenêtre (retour sur l'onglet)
  //   2. `visibilitychange` (retour au tab dans le même browser)
  //   3. Événement custom `favorites:updated` dispatché par les callers
  //      (POST/DELETE /api/favorites) — voir `src/lib/favoritesEvent.js`.
  useEffect(() => {
    let cancelled = false;
    // Eric 2026-07-28 (iter169/170) — Refetch live :
    //  - `recommendations:invalidate` → refetch /api/recommendations
    //  - `progress:invalidate` → refetch /api/user/progress (MiniStats hero)
    // Debounce 400 ms pour laisser le backend persister avant recalcul.
    let recoDebounceId = null;
    let progDebounceId = null;
    const refetchRecos = () => {
      clearTimeout(recoDebounceId);
      recoDebounceId = setTimeout(async () => {
        try {
          const r = await axios.get(`${API}/api/recommendations`, { withCredentials: true });
          if (!cancelled) setRecommendations(r.data);
        } catch { /* silencieux */ }
      }, 400);
    };
    const refetchProgress = () => {
      clearTimeout(progDebounceId);
      progDebounceId = setTimeout(async () => {
        try {
          const r = await axios.get(`${API}/api/user/progress`, { withCredentials: true });
          if (!cancelled) setProgress(r.data);
        } catch { /* silencieux */ }
      }, 400);
    };
    window.addEventListener("recommendations:invalidate", refetchRecos);
    window.addEventListener("progress:invalidate", refetchProgress);
    return () => {
      cancelled = true;
      clearTimeout(recoDebounceId);
      clearTimeout(progDebounceId);
      window.removeEventListener("recommendations:invalidate", refetchRecos);
      window.removeEventListener("progress:invalidate", refetchProgress);
    };
  }, []);

  useEffect(() => {
    let cancelled = false;
    const refetch = async () => {
      try {
        const r = await axios.get(`${API}/api/favorites`, { withCredentials: true });
        if (cancelled) return;
        const arr = Array.isArray(r.data) ? r.data : [];
        setFavorites((prev) => {
          if (prev.length > 0) {
            const prevSet = new Set(prev.map((f) => f.id));
            const newIds = arr.filter((f) => !prevSet.has(f.id)).map((f) => f.id);
            if (newIds.length > 0) {
              setPulsingFavIds((cur) => {
                const next = new Set(cur);
                newIds.forEach((id) => next.add(id));
                return next;
              });
              setTimeout(() => {
                setPulsingFavIds((cur) => {
                  const next = new Set(cur);
                  newIds.forEach((id) => next.delete(id));
                  return next;
                });
              }, 2000);
            }
          }
          return arr;
        });
      } catch { /* silencieux */ }
    };
    const onVis = () => { if (!document.hidden) refetch(); };
    window.addEventListener("focus", refetch);
    window.addEventListener("favorites:updated", refetch);
    document.addEventListener("visibilitychange", onVis);
    return () => {
      cancelled = true;
      window.removeEventListener("focus", refetch);
      window.removeEventListener("favorites:updated", refetch);
      document.removeEventListener("visibilitychange", onVis);
    };
  }, []);

  const removeFavorite = async (type, itemId) => {
    try {
      await axios.delete(`${API}/api/favorites/${type}/${itemId}`, { withCredentials: true });
      setFavorites(favorites.filter(f => !(f.item_type === type && f.item_id === itemId)));
      // B589 — event global (redondant avec l'intercepteur, mais garantit un
      // refresh cross-tab si le back propagate quoi que ce soit).
      window.dispatchEvent(new Event("favorites:updated"));
    } catch (e) { /* silent — non-critical */ }
  };

  const handleReprocess = async (videoId) => {
    setReprocessing(videoId);
    try {
      await axios.post(`${API}/api/videos/${videoId}/reprocess`, {}, { withCredentials: true });
      setTimeout(() => loadData(), 1000);
    } catch (e) { /* silent */ } finally { setReprocessing(null); }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <SpinnerGap size={32} className="animate-spin text-[#002FA7]" />
      </div>
    );
  }

  const gam = progress?.gamification;
  const mastery = knowledgeGaps?.overall_mastery_score || 0;
  // Empty-org banner: when current org has no data, show a clear hint instead of a sea of zeros.
  const isEmptyOrg = !loading && stats && (stats?.videos?.total ?? 0) === 0 && (stats?.knowledge?.total ?? 0) === 0;

  return (
    <div className="space-y-5 animate-fade-in stagger-reveal" data-testid="dashboard-root">
      {isEmptyOrg && (
        <div
          className="rounded-xl border border-amber-200 bg-amber-50 dark:bg-amber-950/30 dark:border-amber-900/60 px-4 py-3 flex items-start sm:items-center gap-3"
          data-testid="empty-org-banner"
        >
          <div className="w-8 h-8 rounded-lg bg-amber-100 dark:bg-amber-900/60 flex items-center justify-center flex-shrink-0">
            <Warning size={18} className="text-amber-700 dark:text-amber-300" />
          </div>
          <div className="flex-1 min-w-0">
            <p className="text-sm font-semibold text-amber-900 dark:text-amber-200">
              {tr("Cette organisation est vide", "This organization is empty")}
            </p>
            <p className="text-xs text-amber-800/80 dark:text-amber-300/80 mt-0.5">
              {tr("Aucune vidéo ni connaissance n'a encore été ajoutée à cette organisation. Pour voir tes données, change d'organisation via le sélecteur en haut, ou importe une vidéo.",
                  "No video or knowledge has been added to this organization yet. To see your data, switch organization from the selector at the top, or import a video.")}
            </p>
          </div>
          <Link
            to="/dashboard/import-video"
            className="hidden sm:inline-flex items-center gap-1.5 text-xs font-semibold text-amber-900 dark:text-amber-200 hover:text-amber-700 underline decoration-dotted underline-offset-4"
            data-testid="empty-org-import-link"
          >
            {tr("Importer une vidéo", "Import a video")}
            <ArrowRight size={12} />
          </Link>
        </div>
      )}
      {/* ── Ta prochaine action (P0 iter — Eric 2026-07-24)
          Insertion en tête, au-dessus du hero, pour orienter TOUS les rôles
          (surtout les apprenants qui voyaient auparavant une liste plate de
          métriques sans CTA clair). Logique de priorité :
            1. Vidéo non vue → Regarder
            2. Thème faible détecté → Faire un quiz de renforcement
            3. Aucun favori → Explorer la base
            4. Fallback : "Tu es à jour" */}
      <NextActionCard
        recommendations={recommendations}
        knowledgeGaps={knowledgeGaps}
        favoritesCount={favorites.length}
        lang={lang}
        t={t}
      />

      {/* ── Hero Brand Banner ── */}
      <div className="relative overflow-hidden rounded-2xl bg-gradient-to-br from-[#001030] via-[#001845] to-[#002FA7] p-6 text-white" data-testid="hero-banner">
        {/* Decorative grid */}
        <div className="absolute inset-0 opacity-[0.04]" style={{ backgroundImage: "linear-gradient(rgba(255,255,255,0.3) 1px, transparent 1px), linear-gradient(90deg, rgba(255,255,255,0.3) 1px, transparent 1px)", backgroundSize: "40px 40px" }} />
        <div className="absolute top-0 right-0 w-72 h-72 bg-blue-500/10 rounded-full blur-3xl -translate-y-1/2 translate-x-1/2" />

        <div className="relative flex flex-col sm:flex-row sm:items-start sm:justify-between gap-4">
          <div className="flex items-center gap-3 sm:gap-4 min-w-0">
            <div className="w-10 h-10 sm:w-14 sm:h-14 rounded-xl bg-white/10 backdrop-blur-sm border border-white/10 flex items-center justify-center overflow-hidden flex-shrink-0">
              <img src={KNORIA_LOGO_URL} alt="KNORIA" className="h-8 sm:h-12 object-contain" />
            </div>
            <div className="min-w-0">
              <div className="flex flex-wrap items-center gap-2">
                <h1 className="text-lg sm:text-2xl font-bold font-['Outfit'] tracking-tight">
                  {t("welcome")}, {user?.name?.split(' ')[0]}
                </h1>
                {user?.is_founder ? (
                  <span className="px-3 py-1 text-xs font-black bg-gradient-to-r from-[#38BDF8]/20 via-[#60A5FA]/15 to-[#002FA7]/20 text-[#38BDF8] rounded-md border border-[#38BDF8]/30 tracking-wide flex items-center gap-1.5 gradient-shift" data-testid="founder-badge">
                    <svg width="14" height="14" viewBox="0 0 200 200"><defs><radialGradient id="bdgG" cx="35%" cy="35%" r="65%"><stop offset="0%" stopColor="#38BDF8"/><stop offset="100%" stopColor="#002FA7"/></radialGradient></defs><circle cx="100" cy="100" r="90" fill="url(#bdgG)"/></svg>
                    {user.display_role}
                  </span>
                ) : user?.display_role ? (
                  <span className="px-2 py-0.5 text-xs font-bold bg-amber-400/20 text-amber-300 rounded-md border border-amber-400/30">
                    {user.display_role}
                  </span>
                ) : null}
              </div>
              <p className="text-blue-200 text-sm mt-0.5">
                {new Date().toLocaleDateString(lang === "fr" ? "fr-CA" : "en-CA", { weekday: "long", year: "numeric", month: "long", day: "numeric" })}
              </p>
              <p className="text-blue-300/60 text-[10px] mt-1 uppercase tracking-widest font-medium">Knowledge Empowered</p>
            </div>
          </div>

          <div className="flex flex-wrap items-center gap-2 sm:gap-4 mt-4 sm:hidden">
            {/* Vincent #264/#265 (2026-02-06) — Retiré du Tableau de bord :
                bouton "Importer vidéo" (réservé aux Concepteurs+, déjà dans
                sidebar) et "Rapport PDF" (jugé non pertinent ici). Bandeau
                hero affiche maintenant uniquement le score IA. */}
          </div>

          <div className="hidden sm:flex items-center gap-4">
            {/* AI Mastery Score */}
            <div className="text-center" data-testid="mastery-score">
              <div className="relative w-20 h-20">
                <svg className="w-20 h-20 -rotate-90" viewBox="0 0 80 80">
                  <circle cx="40" cy="40" r="34" fill="none" stroke="rgba(255,255,255,0.1)" strokeWidth="6" />
                  <circle cx="40" cy="40" r="34" fill="none" stroke={mastery >= 70 ? "#22c55e" : mastery >= 40 ? "#eab308" : "#ef4444"} strokeWidth="6" strokeLinecap="round"
                    strokeDasharray={`${(mastery / 100) * 213.6} 213.6`} className="transition-all duration-1000" />
                </svg>
                <div className="absolute inset-0 flex items-center justify-center">
                  <span className="text-xl font-bold font-['Outfit']">{mastery}%</span>
                </div>
              </div>
              <p className="text-[10px] text-blue-200 mt-1">{t("ai_score")}</p>
            </div>
            {/* Vincent #264/#265 (2026-02-06) — Retiré boutons "PDF" et "Importer
                vidéo" du hero du tableau de bord. */}
          </div>
        </div>

        {/* Quick stats bar */}
        {progress && (() => {
          const weekLabel = lang === "en" ? "this week" : "cette semaine";
          return (
            <div className="relative mt-5 grid grid-cols-2 sm:grid-cols-5 gap-3">
              <MiniStat icon={VideoCamera} value={progress.videos_viewed} label={t("videos_watched")} suffix={`/${progress.total_videos}`} delta={progress.videos_viewed_this_week} deltaLabel={weekLabel} secondary={typeof progress.videos_quiz_passed === "number" ? `${progress.videos_quiz_passed} quiz` : null} />
              <MiniStat icon={Exam} value={progress.quizzes_completed} label={t("quiz")} delta={progress.quizzes_completed_this_week} deltaLabel={weekLabel} />
              <MiniStat icon={ChatCircle} value={progress.chat_queries} label={t("questions_asked")} delta={progress.chat_queries_this_week} deltaLabel={weekLabel} />
              <MiniStat icon={Heart} value={progress.favorites} label={t("favorites")} delta={progress.favorites_this_week} deltaLabel={weekLabel} />
              <MiniStat icon={Lightning} value={gam?.total_points || 0} label={`${t("level")} ${gam?.level || 1}`} highlight />
            </div>
          );
        })()}
      </div>

      {/* ── Quota widget (hidden for founder_exempt) ── */}
      {usage && !usage.founder_exempt && usage.metrics?.videos_uploaded && usage.metrics.videos_uploaded.limit > 0 && (() => {
        const m = usage.metrics.videos_uploaded;
        const pct = m.percent || 0;
        const blocked = !m.allowed;
        const warn = pct >= 80 && !blocked;
        const barColor = blocked ? "bg-red-500" : warn ? "bg-amber-500" : "bg-teal-500";
        const tone = blocked
          ? "border-red-200 bg-red-50 dark:bg-red-950/30 dark:border-red-900/50"
          : warn
          ? "border-amber-200 bg-amber-50 dark:bg-amber-950/30 dark:border-amber-900/50"
          : "border-teal-200 bg-teal-50 dark:bg-teal-950/30 dark:border-teal-900/50";
        const textTone = blocked
          ? "text-red-900 dark:text-red-200"
          : warn
          ? "text-amber-900 dark:text-amber-200"
          : "text-teal-900 dark:text-teal-200";
        return (
          <div className={`rounded-xl border ${tone} p-4`} data-testid="dashboard-quota-widget">
            <div className="flex items-start gap-4">
              <div className={`w-10 h-10 rounded-lg flex items-center justify-center flex-shrink-0 ${blocked ? "bg-red-100 dark:bg-red-900/50" : warn ? "bg-amber-100 dark:bg-amber-900/50" : "bg-teal-100 dark:bg-teal-900/50"}`}>
                <Gauge size={20} weight="duotone" className={textTone} />
              </div>
              <div className="flex-1 min-w-0">
                <div className="flex items-center justify-between gap-3 flex-wrap">
                  <p className={`text-sm font-semibold ${textTone}`}>
                    {lang === "fr" ? "Quota vidéos ce mois" : "Video quota this month"}
                    <span className="ml-2 text-xs opacity-75">
                      ({usage.plan_name || usage.plan})
                    </span>
                  </p>
                  <span className={`font-mono font-semibold text-sm ${textTone}`} data-testid="dashboard-quota-numbers">
                    {m.current} / {m.limit}
                  </span>
                </div>
                <div className="w-full h-2 bg-white/60 dark:bg-black/20 rounded overflow-hidden mt-2">
                  <div className={`h-full ${barColor} transition-all`} style={{ width: `${Math.min(100, pct)}%` }} />
                </div>
                <div className="mt-2.5 flex items-center justify-between gap-3 flex-wrap text-xs">
                  <p className={textTone}>
                    {blocked ? (
                      lang === "fr"
                        ? "Quota atteint — impossible de téléverser d'autres vidéos ce mois-ci."
                        : "Quota reached — cannot upload more videos this month."
                    ) : warn ? (
                      lang === "fr"
                        ? `Plus que ${m.limit - m.current} vidéos avant la limite mensuelle.`
                        : `Only ${m.limit - m.current} videos left before monthly limit.`
                    ) : (
                      lang === "fr"
                        ? `Encore ${m.limit - m.current} vidéos disponibles ce mois.`
                        : `${m.limit - m.current} videos still available this month.`
                    )}
                  </p>
                  {(blocked || warn) && (
                    <Link
                      to="/dashboard/billing"
                      className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg bg-[#002FA7] text-white font-semibold hover:bg-[#001F7A] transition-colors"
                      data-testid="dashboard-quota-upgrade-btn"
                    >
                      {lang === "fr" ? "Passer à un plan supérieur" : "Upgrade plan"}
                      <ArrowRight size={12} />
                    </Link>
                  )}
                </div>
              </div>
            </div>
          </div>
        );
      })()}

      {/* ── Badges Row ── */}
      {gam?.badges_earned?.length > 0 && (
        <div className="flex items-center gap-2 flex-wrap" data-testid="badges-row">
          <span className="text-xs font-medium text-muted-foreground mr-1">{tr("Badges :", "Badges:")}</span>
          {/* Tâche 1422 — le nom ET la description du badge viennent du serveur
              avec leurs deux langues (`name_en` / `description_en`). Repli sur
              le français : un badge ajouté sans traduction doit s'afficher,
              pas laisser un trou. */}
          {gam.badges_earned.map((badge) => (
            <span key={badge.id} className="inline-flex items-center gap-1.5 px-2.5 py-1 bg-amber-50 dark:bg-amber-500/10 border border-amber-200 dark:border-amber-500/20 rounded-full text-xs font-medium text-amber-700 dark:text-amber-300" title={lang === "fr" ? badge.description : (badge.description_en || badge.description)} data-testid={`dashboard-badge-${badge.id}`}>
              <Star size={12} weight="fill" className="text-amber-500" />
              {lang === "fr" ? badge.name : (badge.name_en || badge.name)}
            </span>
          ))}
          <Link to="/dashboard/leaderboard" className="text-xs text-primary hover:underline ml-1">{tr("Voir tout", "View all")}</Link>
        </div>
      )}

      {/* Vincent B995 (Eric 2026-02-26 SPEC STRICK PROD) — Nuage de tags
          pondéré, visible uniquement pour les concepteurs+ qui gèrent
          le vocabulaire. Placé sous les badges pour rester au-dessus
          du fold sans écraser la CTA principale. */}
      {canUploadVideos && <TagCloud />}

      {/* ── AI Personal Coach ── */}
      {coachTips && coachTips.length > 0 && (
        <div className="bg-card border border-border rounded-xl p-5" data-testid="ai-coach-section">
          <div className="flex items-center gap-2 mb-3">
            <div className="w-8 h-8 rounded-lg bg-gradient-to-br from-teal-500 to-teal-500 flex items-center justify-center">
              <Lightbulb size={16} weight="fill" className="text-white" />
            </div>
            <div>
              <h2 className="font-semibold font-['Outfit'] text-foreground text-sm">{t("ai_coach")}</h2>
              <p className="text-[11px] text-muted-foreground">{t("ai_coach_subtitle")}</p>
            </div>
          </div>
          <div className="space-y-2">
            {coachTips.map((tip, i) => (
              <div key={i} className={`flex items-start gap-3 p-3 rounded-lg border ${
                tip.priority === "high" ? "border-rose-200 dark:border-rose-500/20 bg-rose-50/50 dark:bg-rose-500/5" :
                tip.priority === "medium" ? "border-amber-200 dark:border-amber-500/20 bg-amber-50/50 dark:bg-amber-500/5" :
                "border-border bg-secondary/30"
              }`} data-testid={`coach-tip-${i}`}>
                <div className={`w-2 h-2 rounded-full mt-1.5 flex-shrink-0 ${
                  tip.priority === "high" ? "bg-rose-500" : tip.priority === "medium" ? "bg-amber-500" : "bg-teal-500"
                }`} />
                <div className="flex-1 min-w-0">
                  <p className="text-sm">{lang === "en" ? tip.message_en : tip.message_fr}</p>
                </div>
                {tip.action && (
                  <Link to={tip.action} className="flex-shrink-0 text-xs text-primary font-medium hover:underline">
                    {lang === "en" ? tip.action_label_en : tip.action_label_fr}
                  </Link>
                )}
              </div>
            ))}
          </div>
        </div>
      )}

      {/* ── Ta progression (P1 iter — Eric 2026-07-24)
          Refactor de l'ex-"Détecteur de Lacunes IA" :
           - Titre + libellés parlants ("Vidéos vues" vs "Couverture vidéo").
           - Chaque GapMeter devient cliquable → mène directement à l'action.
           - La ligne "Types non explorés" (puces stériles) est remplacée
             par UNE seule suggestion contextuelle avec CTA. */}
      {knowledgeGaps && (
        <div className="bg-card border border-border rounded-xl p-5" data-testid="knowledge-gaps-section">
          <div className="flex items-center gap-2 mb-4">
            <div className="w-8 h-8 rounded-lg bg-[#002FA7]/10 flex items-center justify-center">
              <Target size={16} weight="fill" className="text-[#002FA7]" />
            </div>
            <div>
              <h2 className="font-semibold font-['Outfit'] text-foreground text-sm">{t("ai_gap_detector")}</h2>
              <p className="text-[11px] text-muted-foreground">{t("ai_gap_subtitle")}</p>
            </div>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 mb-4">
            <GapMeter to="/dashboard/knowledge" label={t("video_coverage")} value={knowledgeGaps.video_coverage} testId="gap-videos" />
            <GapMeter to="/dashboard/chat" label={t("knowledge_engagement")} value={knowledgeGaps.knowledge_engagement} testId="gap-interactions" />
            <GapMeter to="/dashboard/quiz" label={t("quiz_mastery")} value={knowledgeGaps.quiz_mastery} testId="gap-quiz" />
          </div>

          {(() => {
            const first = (knowledgeGaps.unexplored_types || [])
              .filter((tt) => tt.type !== "rebuttal")
              .sort((a, b) => (b.count || 0) - (a.count || 0))[0];
            if (!first) return null;
            // Tâche 1324, vague 2 — les deux langues sur la même ligne.
            // Deux tables jumelles divergent au premier type ajouté.
            const TYPE_LABELS = {
              procedure: ["Procédures", "Procedures"],
              concept: ["Concepts", "Concepts"],
              concepts: ["Concepts", "Concepts"],
              objection: ["Objections", "Objections"],
              qa: ["Q&R", "Q&A"],
              script: ["Scripts", "Scripts"],
            };
            const label = (TYPE_LABELS[first.type] || [first.type, first.type])[lang === "en" ? 1 : 0];
            return (
              <Link
                to="/dashboard/knowledge"
                className="flex items-center gap-3 p-3 rounded-lg border border-amber-200 dark:border-amber-500/20 bg-amber-50/60 dark:bg-amber-500/5 hover:bg-amber-100/70 dark:hover:bg-amber-500/10 transition-colors group"
                data-testid="unexplored-hint-cta"
              >
                <div className="w-9 h-9 rounded-lg bg-amber-100 dark:bg-amber-500/20 flex items-center justify-center flex-shrink-0">
                  <Lightbulb size={16} weight="fill" className="text-amber-600 dark:text-amber-400" />
                </div>
                <div className="flex-1 min-w-0">
                  <p className="text-sm">
                    <span className="text-muted-foreground">{t("unexplored_hint_prefix")}</span>{" "}
                    <span className="font-semibold text-foreground">{label}</span>
                    <span className="text-muted-foreground"> ({first.count})</span>
                  </p>
                </div>
                <span className="text-xs font-semibold text-[#002FA7] dark:text-amber-300 group-hover:underline flex items-center gap-1 flex-shrink-0">
                  {t("unexplored_hint_cta")} <ArrowRight size={12} />
                </span>
              </Link>
            );
          })()}
        </div>
      )}

      {/* ── AI Recommendations ── */}
      {recommendations && (recommendations.unwatched_videos?.length > 0 || recommendations.trending_topics?.length > 0 || recommendations.popular_videos?.length > 0) && (
        <div className="bg-card border border-border rounded-xl p-5" data-testid="recommendations-section">
          <div className="flex items-center gap-2 mb-4">
            <div className="w-8 h-8 rounded-lg bg-gradient-to-br from-violet-500 to-blue-500 flex items-center justify-center">
              <Sparkle size={16} weight="fill" className="text-white" />
            </div>
            <div>
              <h2 className="font-semibold font-['Outfit'] text-foreground text-sm">{t("recommended_for_you")}</h2>
              <p className="text-[11px] text-muted-foreground">{t("recommended_subtitle")}</p>
            </div>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
            {recommendations.unwatched_videos?.map((v) => (
              <Link key={v.id} to={`/dashboard/video/${v.id}`} className="flex items-center gap-3 p-3 rounded-lg border border-border hover:bg-secondary/50 transition-colors group" data-testid={`rec-video-${v.id}`}>
                <div className="w-9 h-9 rounded-lg bg-blue-500/10 flex items-center justify-center flex-shrink-0 group-hover:bg-blue-500/20 transition-colors">
                  <Play size={16} weight="fill" className="text-blue-500" />
                </div>
                <div className="min-w-0">
                  <p className="text-sm font-medium truncate text-foreground">{v.title}</p>
                  <p className="text-[10px] text-muted-foreground">{t("not_watched")}</p>
                </div>
              </Link>
            ))}
            {recommendations.popular_videos?.map((v) => (
              <Link key={`pop-${v.id}`} to={`/dashboard/video/${v.id}`} className="flex items-center gap-3 p-3 rounded-lg border border-border hover:bg-secondary/50 transition-colors group" data-testid={`rec-popular-${v.id}`}>
                <div className="w-9 h-9 rounded-lg bg-amber-500/10 flex items-center justify-center flex-shrink-0 group-hover:bg-amber-500/20 transition-colors">
                  <TrendUp size={16} weight="fill" className="text-amber-500" />
                </div>
                <div className="min-w-0">
                  <p className="text-sm font-medium truncate text-foreground">{v.title}</p>
                  <p className="text-[10px] text-muted-foreground">{t("popular")} - {v.view_count} vues</p>
                </div>
              </Link>
            ))}
            {recommendations.trending_topics?.length > 0 && (
              <Link to="/dashboard/chat" className="flex items-center gap-3 p-3 rounded-lg border border-border hover:bg-secondary/50 transition-colors group" data-testid="rec-trending">
                <div className="w-9 h-9 rounded-lg bg-teal-500/10 flex items-center justify-center flex-shrink-0 group-hover:bg-teal-500/20 transition-colors">
                  <ChatCircle size={16} weight="fill" className="text-teal-500" />
                </div>
                <div className="min-w-0">
                  <p className="text-sm font-medium text-foreground">{t("trending_questions")}</p>
                  <p className="text-[10px] text-muted-foreground truncate">{recommendations.trending_topics[0]}</p>
                </div>
              </Link>
            )}
          </div>
        </div>
      )}

      {/* Vincent B788 (Eric 2026-02-18 SPEC STRICK PROD) — Widget
          « Vidéos suspectes » : compte les vidéos > 5 min avec < 20
          segments Whisper (heuristique aggressive_dedup / MP3 tronqué).
          Uniquement visible pour les éditeurs, uniquement si count > 0
          (silencieux si tout va bien). Style ambre pulsant pour attirer
          l'œil, lien direct vers la page admin qui liste + rerun batch. */}
      {canUploadVideos && suspectCount && suspectCount.count > 0 && (
        <Link
          to="/dashboard/admin/aggressive-dedup"
          className="block bg-amber-50 border-2 border-amber-400 rounded-xl p-4 hover:bg-amber-100 hover:border-amber-500 transition-colors group shadow-sm hover:shadow-md"
          data-testid="suspect-videos-widget"
        >
          <div className="flex items-center gap-4">
            <div className="flex-shrink-0 w-12 h-12 rounded-lg bg-amber-200 flex items-center justify-center text-2xl">
              <span aria-hidden="true">⚠️</span>
            </div>
            <div className="flex-1 min-w-0">
              <p className="text-xs uppercase tracking-wide text-amber-700 font-bold mb-0.5">
                {lang === "fr" ? "Diagnostic transcription" : "Transcription diagnostic"}
              </p>
              <p className="text-sm font-semibold text-amber-900" data-testid="suspect-videos-count">
                {suspectCount.count === 1
                  ? (lang === "fr" ? "1 vidéo suspecte détectée" : "1 suspect video detected")
                  : (lang === "fr"
                    ? `${suspectCount.count} vidéos suspectes détectées`
                    : `${suspectCount.count} suspect videos detected`)}
              </p>
              <p className="text-[11px] text-amber-800 leading-snug mt-0.5">
                {lang === "fr"
                  ? "Vidéos > 5 min avec < 20 segments Whisper (typique d'un filtre anti-hallucination agressif)."
                  : "> 5-min videos with < 20 Whisper segments (typical of an aggressive anti-hallucination filter)."}
                {suspectCount.top_examples?.[0] && (
                  <>
                    {" "}
                    <span className="italic">
                      {lang === "fr" ? "La plus longue : " : "Longest: "}
                      <span className="font-mono tabular-nums">
                        {Math.floor((suspectCount.top_examples[0].duration_seconds || 0) / 60)}m {String(Math.round((suspectCount.top_examples[0].duration_seconds || 0) % 60)).padStart(2, "0")}s
                      </span>
                    </span>
                  </>
                )}
              </p>
            </div>
            <div className="flex-shrink-0 text-amber-700 group-hover:text-amber-900 transition-colors">
              <ArrowRight size={20} />
            </div>
          </div>
        </Link>
      )}

      {/* ── Admin Stats ── */}
      {isAdmin && stats && (
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
          <StatCard icon={VideoCamera} label={tr("Vidéos au total", "Total videos")} value={stats.videos.total} sublabel={tr(`${stats.videos.processing} en traitement`, `${stats.videos.processing} processing`)} />
          <StatCard icon={Brain} label={tr("Connaissances", "Knowledge")} value={stats.knowledge.total} sublabel={tr(`${stats.knowledge.approved} approuvées`, `${stats.knowledge.approved} approved`)} />
          <StatCard icon={ClipboardText} label={tr("À réviser", "To review")} value={stats.knowledge.pending_review} sublabel={tr("En attente", "Pending")} />
          <StatCard icon={Warning} label={tr("Problèmes", "Issues")} value={stats.issues.unresolved} sublabel={tr("Non résolus", "Unresolved")} />
        </div>
      )}

      {/* ── Vincent B700 (Eric 2026-02-10) — Santé fusionnée du catalogue ──
           Combien de synthèses bénéficient déjà des corrections manuelles
           FLHQ vs combien attendent encore une régénération après fusion. */}
      {isAdmin && stats?.transcript_health && (stats.transcript_health.synthesis_total > 0 || stats.transcript_health.videos_non_text_total > 0) && (
        <div className="bg-card border border-border rounded-xl p-5" data-testid="transcript-health-card">
          <div className="flex items-center justify-between mb-3">
            <h3 className="font-medium font-['Outfit']">{tr("Santé fusionnée du catalogue", "Merged health of the catalogue")}</h3>
            <span className="text-xs text-muted-foreground">FLHQ / Loi 25</span>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            {/* Barre 1 — % synthèses générées depuis la fusionnée */}
            <div>
              <div className="flex items-baseline justify-between mb-1">
                <span className="text-xs text-muted-foreground">{tr("Synthèses depuis la fusionnée", "Syntheses from the merged transcript")}</span>
                <span
                  className="text-sm font-semibold"
                  style={{
                    color: stats.transcript_health.synthesis_merged_pct >= 80 ? "#0f766e" :
                           stats.transcript_health.synthesis_merged_pct >= 50 ? "#b45309" : "#b91c1c"
                  }}
                  data-testid="synthesis-merged-pct"
                >
                  {stats.transcript_health.synthesis_merged_pct}%
                </span>
              </div>
              <div className="h-2 bg-zinc-200 rounded-full overflow-hidden">
                <div
                  className="h-full transition-all duration-500"
                  style={{
                    width: `${stats.transcript_health.synthesis_merged_pct}%`,
                    backgroundColor: stats.transcript_health.synthesis_merged_pct >= 80 ? "#14b8a6" :
                                     stats.transcript_health.synthesis_merged_pct >= 50 ? "#f59e0b" : "#ef4444"
                  }}
                />
              </div>
              <p className="text-[11px] text-muted-foreground mt-1">
                {stats.transcript_health.synthesis_from_merged} / {stats.transcript_health.synthesis_total} {tr("synthèses", "syntheses")}
              </p>
            </div>
            {/* Barre 2 — % vidéos ayant une fusion disponible */}
            <div>
              <div className="flex items-baseline justify-between mb-1">
                <span className="text-xs text-muted-foreground">{tr("Vidéos avec fusion disponible", "Videos with a merged transcript")}</span>
                <span
                  className="text-sm font-semibold"
                  style={{
                    color: stats.transcript_health.videos_merged_pct >= 80 ? "#0d9488" :
                           stats.transcript_health.videos_merged_pct >= 50 ? "#d97706" : "#dc2626"
                  }}
                  data-testid="videos-merged-pct"
                >
                  {stats.transcript_health.videos_merged_pct}%
                </span>
              </div>
              <div className="h-2 bg-zinc-200 rounded-full overflow-hidden">
                <div
                  className="h-full transition-all duration-500"
                  style={{
                    width: `${stats.transcript_health.videos_merged_pct}%`,
                    backgroundColor: stats.transcript_health.videos_merged_pct >= 80 ? "#14b8a6" :
                                     stats.transcript_health.videos_merged_pct >= 50 ? "#f59e0b" : "#ef4444"
                  }}
                />
              </div>
              <p className="text-[11px] text-muted-foreground mt-1">
                {stats.transcript_health.videos_with_merged} / {stats.transcript_health.videos_non_text_total} {tr("vidéos", "videos")}
              </p>
            </div>
          </div>
          {/* Détail par source si des synthèses non-fusionnées existent */}
          {stats.transcript_health.synthesis_total - stats.transcript_health.synthesis_from_merged > 0 && (
            <div className="mt-3 pt-3 border-t border-border flex flex-wrap gap-x-4 gap-y-1 text-[11px] text-muted-foreground" data-testid="synthesis-source-breakdown">
              {Object.entries(stats.transcript_health.synthesis_by_source)
                .filter(([src]) => src !== "fusionnée")
                .map(([src, n]) => {
                  const label = libelleSource(src, lang);
                  return <span key={src}>· {label} : <span className="font-medium text-foreground">{n}</span></span>;
                })}
            </div>
          )}
        </div>
      )}

      {/* ── Two Column: Videos + Favorites ── */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-5">
        <div className="lg:col-span-2 bg-card border border-border rounded-xl overflow-hidden">
          <div className="px-5 py-4 border-b border-border flex items-center justify-between">
            <h2 className="font-medium font-['Outfit']">{t("recent_videos")}</h2>
            <Link to="/dashboard/knowledge" className="text-sm text-primary hover:underline flex items-center gap-1" data-testid="view-all-videos-link">
              {t("see_all_link")} <ArrowRight size={14} />
            </Link>
          </div>
          {recentVideos.length > 0 ? (
            <div className="divide-y divide-border">
              {recentVideos.map((video) => (
                <div
                  key={video.id}
                  className="block px-5 py-3.5 hover:bg-secondary/50 transition-colors cursor-pointer"
                  onClick={() => { if (video.pipeline_status === "complete") nav(`/dashboard/video/${video.id}`); }}
                  data-testid={`video-row-${video.id}`}
                >
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-3">
                      <div className="w-10 h-10 bg-secondary rounded-lg flex items-center justify-center">
                        {video.pipeline_status === "complete" ? <Play size={18} className="text-primary" weight="fill" /> : <VideoCamera size={18} className="text-muted-foreground" />}
                      </div>
                      <div>
                        <h3 className="font-medium text-sm">{video.title}</h3>
                        <p className="text-xs text-muted-foreground mt-0.5">{video.training_theme} - {new Date(video.created_at).toLocaleDateString(lang === "fr" ? "fr-CA" : "en-CA")}</p>
                      </div>
                    </div>
                    <div className="flex items-center gap-2">
                      {video.pipeline_status === "failed" && isAdmin && (
                        <Button variant="outline" size="sm" onClick={(e) => { e.preventDefault(); e.stopPropagation(); handleReprocess(video.id); }} disabled={reprocessing === video.id} className="text-xs gap-1" data-testid={`reprocess-btn-${video.id}`}>
                          {reprocessing === video.id ? <SpinnerGap size={14} className="animate-spin" /> : <ArrowClockwise size={14} />}
                          Relancer
                        </Button>
                      )}
                      {/* P2 iter (Eric 2026-07-24) — Le badge "complete/processing/
                          failed" est un statut PIPELINE, jargon technique inutile
                          pour un apprenant. Affiché uniquement aux admins. */}
                      {isAdmin && (
                        <div className={`flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-medium ${
                          video.pipeline_status === "complete" ? "bg-teal-50 text-teal-700 dark:bg-teal-500/10 dark:text-teal-400" :
                          video.pipeline_status === "processing" ? "bg-blue-50 text-blue-700 dark:bg-blue-500/10 dark:text-blue-400" :
                          video.pipeline_status === "failed" ? "bg-red-50 text-red-700 dark:bg-red-500/10 dark:text-red-400" :
                          "bg-secondary text-muted-foreground"
                        }`}>
                          {video.pipeline_status === "complete" ? <CheckCircle size={14} weight="fill" /> : video.pipeline_status === "processing" ? <SpinnerGap size={14} className="animate-spin" /> : video.pipeline_status === "failed" ? <XCircle size={14} /> : <Clock size={14} />}
                          {video.pipeline_status}
                        </div>
                      )}
                      {!isAdmin && (
                        <ArrowRight size={16} className="text-muted-foreground opacity-40" />
                      )}
                    </div>
                  </div>
                </div>
              ))}
            </div>
          ) : (
            <div className="py-12 text-center" data-testid="dashboard-no-video-empty">
              <Rocket size={40} className="mx-auto mb-3 text-muted-foreground opacity-30" />
              <h3 className="font-medium">{t("no_video")}</h3>
              {/* Vincent XLS iter103 (items 24, 34) — Bouton + texte d'incitation
                  uniquement pour les uploaders autorisés. Les apprenants voient
                  juste "Aucune vidéo" sans CTA d'upload. */}
              {canUploadVideos && (
                <>
                  <p className="text-sm text-muted-foreground mt-1">{t("import_first")}</p>
                  <Link to="/dashboard/upload" className="mt-3 inline-flex items-center gap-2 px-4 py-2 bg-primary text-primary-foreground text-sm rounded-lg hover:opacity-90" data-testid="upload-first-video-btn">
                    <VideoCamera size={16} /> Importer
                  </Link>
                </>
              )}
            </div>
          )}
        </div>

        {/* Favorites */}
        <div className="bg-card border border-border rounded-xl overflow-hidden">
          <div className="px-5 py-4 border-b border-border flex items-center gap-2">
            <Heart size={18} className="text-rose-500" weight="fill" />
            <h2 className="font-medium font-['Outfit']">{t("my_favorites")}</h2>
            <span className="ml-auto text-xs text-muted-foreground">{favorites.length}</span>
          </div>
          <div className="max-h-[400px] overflow-y-auto">
            {favorites.length > 0 ? (
              <div className="divide-y divide-border">
                {favorites.slice(0, 10).map((fav) => {
                  // Vincent B583 (Eric 2026-07-27 18h15 SPEC STRICK) — Types
                  // autorisés STRICTEMENT (aucun « Connaissance » générique) :
                  //   Q&R, Script de vente, Procédure, Concept, Objection, Mot du dictionnaire.
                  // Les liens URL doivent pointer vers l'onglet correspondant
                  // de la Base de connaissances (ou la page dédiée).
                  const KB_SUBTYPE_LABELS = {
                    "qa": ["Q&R", "Q&A"],
                    "q&r": ["Q&R", "Q&A"],
                    "qr": ["Q&R", "Q&A"],
                    "question": ["Q&R", "Q&A"],
                    "objection": ["Objection", "Objection"],
                    "rebuttal": ["Objection", "Objection"],
                    "procedure": ["Procédure", "Procedure"],
                    "procédure": ["Procédure", "Procedure"],
                    "concept": ["Concept", "Concept"],
                    "script": ["Script de vente", "Sales script"],
                    "sales-script": ["Script de vente", "Sales script"],
                    "sales_script": ["Script de vente", "Sales script"],
                  };
                  // Vincent B590 (Eric 2026-07-29 SPEC STRICK) — Mapping
                  // strict des sous-types de connaissance vers les onglets
                  // exacts de VideoDetailPage (activePanel) :
                  //   - qa           → tab=qa
                  //   - script       → tab=scripts (pluriel côté panel)
                  //   - procedure    → tab=procedures (pluriel côté panel)
                  //   - objection    → tab=objections (pluriel côté panel).
                  //     Vincent B621 (Eric 2026-07-30) — Auparavant on redirigeait
                  //     vers `knowledge` (qui rend les Concepts), la connaissance
                  //     Objection ciblée n'apparaissait donc jamais dans le DOM
                  //     et le focus échouait silencieusement.
                  //   - concept      → tab=knowledge
                  //   - dictionary   → route /dashboard/dictionary séparée
                  const KB_SUBTYPE_VIDEO_TABS = {
                    "qa": "qa",
                    "q&r": "qa",
                    "qr": "qa",
                    "question": "qa",
                    "objection": "objections",
                    "rebuttal": "objections",
                    "procedure": "procedures",
                    "procédure": "procedures",
                    "concept": "knowledge",
                    "script": "scripts",
                    "sales-script": "scripts",
                    "sales_script": "scripts",
                  };
                  // Vincent B590 (Eric 2026-07-29 SPEC STRICK) — Mapping
                  // séparé pour KnowledgePage (fallback quand la connaissance
                  // n'a pas de video_id source). Les tabs valides KnowledgePage
                  // sont : qa, script, procedure, concept, objection, rebuttal.
                  // Sans ce mapping distinct, objection/concept aboutissaient
                  // sur ?tab=knowledge (invalide côté KnowledgePage) → l'onglet
                  // ne se sélectionnait pas et le focus ne trouvait rien.
                  const KB_SUBTYPE_KB_TABS = {
                    "qa": "qa",
                    "q&r": "qa",
                    "qr": "qa",
                    "question": "qa",
                    "objection": "objection",
                    "rebuttal": "rebuttal",
                    "procedure": "procedure",
                    "procédure": "procedure",
                    "concept": "concept",
                    "script": "script",
                    "sales-script": "script",
                    "sales_script": "script",
                  };
                  // Compatibilité : ancienne référence à KB_SUBTYPE_TABS.
                  const KB_SUBTYPE_TABS = KB_SUBTYPE_VIDEO_TABS;
                  let typeLabel;
                  let href;
                  if (fav.item_type === "video") {
                    typeLabel = tr("Vidéo", "Video");
                    href = `/dashboard/video/${fav.item_id}`;
                  } else if (fav.item_type === "sales_script") {
                    typeLabel = tr("Script de vente", "Sales script");
                    // B590 — Si le script est rattaché à une vidéo source,
                    // ouvre directement la vidéo à l'onglet Scripts + focus.
                    href = fav.video_id
                      ? `/dashboard/video/${fav.video_id}?tab=scripts&focus=${fav.item_id}`
                      : `/dashboard/knowledge?tab=script&focus=${fav.item_id}`;
                  } else if (fav.item_type === "dictionary_term") {
                    typeLabel = tr("Mot du dictionnaire", "Dictionary term");
                    href = `/dashboard/dictionary?focus=${fav.item_id}`;
                  } else if (fav.item_type === "knowledge") {
                    const subtype = (fav.knowledge_type || "").toLowerCase();
                    typeLabel = (KB_SUBTYPE_LABELS[subtype] || [])[lang === "en" ? 1 : 0];
                    if (!typeLabel) {
                      // Sous-type inconnu — on log pour investigation mais on
                      // affiche quand même le sous-type brut plutôt que le
                      // fallback générique « Connaissance » (interdit par B583).
                      typeLabel = subtype || "—";
                    }
                    // Vincent B590 (Eric 2026-07-29 SPEC STRICK) — Deux
                    // mappings distincts : VideoDetailPage (activePanel) vs
                    // KnowledgePage (activeTab). Si la connaissance a une
                    // vidéo source → ouvre la vidéo à l'onglet dédié. Sinon
                    // fallback vers /dashboard/knowledge avec le tab KB.
                    const videoTab = KB_SUBTYPE_VIDEO_TABS[subtype];
                    const kbTab = KB_SUBTYPE_KB_TABS[subtype];
                    if (fav.video_id && videoTab) {
                      href = `/dashboard/video/${fav.video_id}?tab=${videoTab}&focus=${fav.item_id}`;
                    } else if (kbTab) {
                      href = `/dashboard/knowledge?tab=${kbTab}&focus=${fav.item_id}`;
                    } else {
                      href = `/dashboard/knowledge?focus=${fav.item_id}`;
                    }
                  } else {
                    typeLabel = fav.item_type || "—";
                    href = "/dashboard/knowledge";
                  }
                  return (
                    <Link
                      key={fav.id}
                      to={href}
                      className={`px-4 py-3 flex items-center gap-3 group hover:bg-secondary/50 transition-colors ${pulsingFavIds.has(fav.id) ? "ring-2 ring-teal-400/60 animate-pulse rounded" : ""}`}
                      data-testid={`favorite-${fav.id}`}
                    >
                      <div className={`w-7 h-7 rounded flex items-center justify-center flex-shrink-0 ${fav.item_type === "video" ? "bg-blue-500/10" : "bg-teal-500/10"}`}>
                        {fav.item_type === "video" ? <VideoCamera size={14} className="text-blue-500" /> : <Brain size={14} className="text-teal-500" />}
                      </div>
                      <div className="flex-1 min-w-0">
                        <p className="text-sm truncate">{fav.title || "Sans titre"}</p>
                        <p className="text-[10px] text-muted-foreground" data-testid={`favorite-${fav.id}-type`}>{typeLabel}</p>
                      </div>
                      <button
                        onClick={(e) => { e.preventDefault(); e.stopPropagation(); removeFavorite(fav.item_type, fav.item_id); }}
                        className="opacity-0 group-hover:opacity-100 p-1 text-muted-foreground hover:text-destructive transition-all"
                        data-testid={`remove-fav-${fav.id}`}
                      >
                        <Trash size={14} />
                      </button>
                    </Link>
                  );
                })}
              </div>
            ) : (
              <div className="p-6 text-center text-muted-foreground">
                <Heart size={28} className="mx-auto mb-2 opacity-30" />
                <p className="text-sm">{t("no_favorites")}</p>
                <p className="text-xs mt-1">{t("add_favorites_hint")}</p>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Vincent B576 (Eric 2026-07-27) — Section « Problèmes récents »
          réservée aux éditeurs (concepteur, admin, founder, ceo, vp).
          Auparavant guardée par isAdmin qui excluait les concepteurs. */}
      {canUploadVideos && stats?.issues?.recent?.length > 0 && (
        <div className="bg-card border border-border rounded-xl overflow-hidden">
          <div className="px-5 py-4 border-b border-border flex items-center justify-between">
            <h2 className="font-medium font-['Outfit']">{t("recent_problems")}</h2>
            <Link to="/dashboard/admin/analytics" className="text-sm text-primary hover:underline flex items-center gap-1" data-testid="view-all-issues-link">{t("see_all_link")} <ArrowRight size={14} /></Link>
          </div>
          <div className="divide-y divide-border">
            {stats.issues.recent.slice(0, 3).map((issue) => (
              <div key={issue.id} className="px-5 py-3.5 hover:bg-secondary/50 transition-colors">
                <div className="flex items-center gap-3">
                  <div className={`w-2 h-2 rounded-full ${issue.severity === 'critical' ? 'bg-red-500' : 'bg-yellow-500'}`} />
                  <div className="flex-1">
                    <p className="text-sm">{issue.message}</p>
                    <p className="text-xs text-muted-foreground mt-0.5">{issue.stage} - {new Date(issue.created_at).toLocaleString(lang === "fr" ? "fr-CA" : "en-CA")}</p>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}

function MiniStat({ icon: Icon, value, label, suffix, highlight, delta, deltaLabel, secondary }) {
  // Eric 2026-07-27 (iter165) — Indicateur temporel discret « +N cette semaine »
  // affiché à côté du chiffre principal quand `delta` > 0.
  // Vincent B586 (Eric 2026-07-27 20h) — 2e chiffre optionnel « · Y quiz »
  // pour afficher les quiz réussis en plus des vidéos vues.
  const showDelta = typeof delta === "number" && delta > 0;
  return (
    <div className={`flex items-center gap-3 px-3 py-2.5 rounded-xl ${highlight ? "bg-amber-400/10 border border-amber-400/20" : "bg-white/5 border border-white/10"}`}>
      <Icon size={18} weight={highlight ? "fill" : "regular"} className={highlight ? "text-amber-300" : "text-blue-200"} />
      <div className="min-w-0">
        <div className="text-base font-bold font-['Outfit'] flex items-baseline gap-1.5">
          <span>{value}<span className="text-xs font-normal text-blue-300">{suffix}</span></span>
          {secondary && (
            <span className="text-xs font-normal text-blue-200/80" data-testid="ministat-secondary">· {secondary}</span>
          )}
          {showDelta && (
            <span
              className="text-[10px] font-semibold text-teal-300 tabular-nums whitespace-nowrap"
              data-testid={`ministat-delta-${(label || "").toLowerCase().replace(/\s+/g, "-")}`}
              title={deltaLabel}
            >
              +{delta}
            </span>
          )}
        </div>
        <div className="text-[10px] text-blue-900/80 font-semibold truncate">
          {label}
          {showDelta && deltaLabel && (
            <span className="text-teal-900/80"> · {deltaLabel}</span>
          )}
        </div>
      </div>
    </div>
  );
}

function GapMeter({ label, value, to, testId }) {
  const color = value >= 70 ? "bg-teal-500" : value >= 40 ? "bg-amber-500" : "bg-rose-500";
  const inner = (
    <>
      <div className="flex justify-between text-xs mb-1">
        <span className="text-muted-foreground">{label}</span>
        <span className="font-semibold">{value}%</span>
      </div>
      <div className="h-2 bg-secondary rounded-full overflow-hidden">
        <div className={`h-full ${color} rounded-full transition-all duration-700`} style={{ width: `${value}%` }} />
      </div>
    </>
  );
  if (to) {
    return (
      <Link
        to={to}
        data-testid={testId}
        className="block p-2 -m-2 rounded-lg hover:bg-secondary/40 transition-colors"
      >
        {inner}
      </Link>
    );
  }
  return <div data-testid={testId}>{inner}</div>;
}

// NextActionCard — P0 iter (Eric 2026-07-24), enrichi B558 (2026-07-25),
// refonte spec Eric 2026-07-27 (iter161) :
//
// Le backend calcule un tableau ORDONNÉ `recommendations.next_actions` selon
// la spec précise Eric :
//   1. Parcours EN COURS (onboarding d'abord) → 1re vidéo non terminée :
//        - kind="resume_video"  → « Reprends la vidéo » (t=seconds_watched)
//        - kind="quiz_video" reason="no_quiz" → « Il est temps de valider »
//        - kind="quiz_video" reason="quiz_below_60" → « Refais le quiz ≥60% »
//   2. kind="start_next_pathway" → 1re vidéo du prochain parcours non commencé.
//   3. kind="popular_unseen" → vidéo non vue populaire (favoris tous users).
//
// La carte affiche EN PREMIER `next_actions[0]`. Le bouton rotation B577
// permet de parcourir les autres suggestions concrètes (jamais des
// « généralités »). Ultimate fallback si aucune : quiz sur un thème faible,
// puis explore/browse.
function NextActionCard({ recommendations, knowledgeGaps, favoritesCount, lang, t }) {
  const nextActions = recommendations?.next_actions || [];
  const weakThemes = knowledgeGaps?.weak_themes || [];

  const suggestions = [];

  // Icônes selon le kind
  const kindIcon = { resume_video: Play, quiz_video: Exam, start_next_pathway: Play, popular_unseen: Star };
  const kindLabels = {
    resume_video: ["Reprends ta vidéo", "Resume your video"],
    quiz_video: ["Valide tes connaissances", "Validate your knowledge"],
    start_next_pathway: ["Commence ton prochain parcours", "Start your next pathway"],
    popular_unseen: ["Découvre une vidéo populaire", "Discover a popular video"],
  };
  const kindCtas = {
    resume_video: ["Reprendre", "Resume"],
    quiz_video: ["Faire le quiz", "Take quiz"],
    start_next_pathway: ["Commencer", "Start"],
    popular_unseen: ["Regarder", "Watch"],
  };
  const iLang = lang === "en" ? 1 : 0;

  // Priorité #1 (backend spec Eric) — suggestions PRÉCISES issues du backend.
  for (const a of nextActions) {
    const kind = a.kind || "resume_video";
    const Icon = kindIcon[kind] || Play;
    const lead = (kindLabels[kind] || [])[iLang] || (a.video?.title || "");
    const cta = (kindCtas[kind] || [])[iLang] || (lang === "en" ? "Open" : "Ouvrir");
    suggestions.push({
      variant: kind,
      lead,
      sub: (lang === "en" ? (a.message_en || a.message) : a.message) || (a.video?.title || ""),
      cta,
      to: a.cta_route || (a.video?.id ? `/dashboard/video/${a.video.id}` : "/dashboard/knowledge"),
      Icon,
      testId: `next-action-${kind.replace(/_/g, "-")}`,
    });
  }

  // Vincent B598 (Eric 2026-07-29 SPEC STRICK) — Toujours proposer au moins
  // 2 suggestions pour que le bouton « prochaine suggestion » soit disponible.
  // Ajout des thèmes faibles + fallback Explorer même si primary existe.
  // Chaque item est dédupliqué par `to` pour éviter les doublons.
  for (const w of weakThemes.slice(0, 3)) {
    const themeName = typeof w === "string" ? w : (w?.theme || "");
    if (!themeName) continue;
    suggestions.push({
      variant: "quiz",
      lead: t("next_action_quiz_lead"),
      sub: `${t("next_action_quiz_sub")} · ${themeName}`,
      cta: t("next_action_btn_quiz"),
      to: "/dashboard/quiz",
      Icon: Exam,
      testId: "next-action-quiz",
    });
  }
  // Vincent B598 (Eric 2026-07-29 SPEC STRICK) — Le bouton de rotation
  // « prochaine suggestion » n'était PAS visible pour les utilisateurs qui
  // n'avaient qu'une seule suggestion résiduelle (canRotate = false). Vincent
  // veut TOUJOURS voir un bouton pour passer à l'action suivante, y compris
  // quand la prochaine action ne lui convient pas. On ajoute donc plusieurs
  // fallbacks systématiques (dashboard, favoris, dictionnaire, chatbot,
  // messagerie) pour garantir ≥ 2 suggestions distinctes après déduplication.
  const universalFallbacks = [
    { variant: "browse-kb", lead: lang === "en" ? "Browse knowledge" : "Explore la base de connaissances", sub: lang === "en" ? "Q&A, scripts, procedures, objections and concepts" : "Q&R, scripts, procédures, objections et concepts", cta: lang === "en" ? "Open" : "Ouvrir", to: "/dashboard/knowledge", Icon: Star, testId: "next-action-browse-kb" },
    { variant: "browse-favorites", lead: lang === "en" ? "Review your favorites" : "Revois tes favoris", sub: lang === "en" ? "The items you saved for later" : "Les éléments que tu as mis de côté", cta: lang === "en" ? "Open" : "Ouvrir", to: "/dashboard", Icon: Heart, testId: "next-action-browse-favorites" },
    { variant: "browse-chatbot", lead: lang === "en" ? "Ask the chatbot" : "Interroge le chatbot", sub: lang === "en" ? "Get an answer in seconds" : "Obtiens une réponse en quelques secondes", cta: lang === "en" ? "Open" : "Ouvrir", to: "/dashboard/chatbot", Icon: Brain, testId: "next-action-browse-chatbot" },
  ];
  for (const fb of universalFallbacks) suggestions.push(fb);

  // Fallback Explorer — TOUJOURS ajouté (dédupliqué juste après).
  suggestions.push({
    variant: "explore",
    lead: t("next_action_explore_lead"),
    sub: t("next_action_explore_sub"),
    cta: t("next_action_btn_explore"),
    to: "/dashboard/knowledge",
    Icon: Star,
    testId: "next-action-explore",
  });
  // Déduplication par `to` (préserve l'ordre — les actions du backend d'abord).
  const seen = new Set();
  const dedup = [];
  for (const s of suggestions) {
    if (seen.has(s.to)) continue;
    seen.add(s.to);
    dedup.push(s);
  }
  suggestions.length = 0;
  suggestions.push(...dedup);

  // Fallback ultime : si absolument rien (aucun next_action + aucun weak_theme +
  // Explorer déjà déduit), on ajoute un browse générique.
  if (suggestions.length === 0) {
    suggestions.push({
      variant: "browse",
      lead: lang === "en" ? "Keep exploring" : "Continue d'explorer",
      sub: lang === "en"
        ? "Browse the knowledge base or your favorites"
        : "Parcours la base de connaissances ou tes favoris",
      cta: lang === "en" ? "Open" : "Ouvrir",
      to: "/dashboard/knowledge",
      Icon: Star,
      testId: "next-action-browse",
    });
  }

  // Rotation client-side (B577 + B598)
  const [suggestionIdx, setSuggestionIdx] = useState(0);
  const idx = suggestions.length > 0 ? (suggestionIdx % suggestions.length) : 0;
  const current = suggestions[idx] || {
    variant: "explore",
    lead: t("next_action_explore_lead"),
    sub: t("next_action_explore_sub"),
    cta: t("next_action_btn_explore"),
    to: "/dashboard/knowledge",
    Icon: Star,
    testId: "next-action-explore",
  };
  const { lead, sub, cta, to, Icon, testId } = current;
  const canRotate = suggestions.length > 1;

  // Vincent B598.b/.c (Eric 2026-07-29) — Indicateur textuel « Autres suggestions »
  // via le hook `useOneTimeHint` (sessionStorage, 8 s auto-timeout).
  const { show: showHint, dismiss: dismissHint } = useOneTimeHint(
    "kn-next-action-rotate-hint-seen", 8000, canRotate
  );

  return (
    <div
      className="relative overflow-hidden rounded-2xl border border-[#002FA7]/15 dark:border-[#38BDF8]/20 bg-gradient-to-br from-[#F5F8FF] via-white to-[#EEF3FF] dark:from-[#001030] dark:via-[#001845] dark:to-[#002FA7]/40 p-5 sm:p-6"
      data-testid="next-action-card"
    >
      <div className="absolute -top-8 -right-8 w-40 h-40 bg-[#002FA7]/5 dark:bg-[#38BDF8]/5 rounded-full blur-3xl" />
      <div className="relative flex flex-col sm:flex-row sm:items-center gap-4">
        {/* Vincent B577+B598 (Eric 2026-07-27→29 SPEC STRICK) — Bouton « Prochaine
            suggestion » avec compteur discret « X / N » (rotation circulaire).
            B598 (Vincent 2026-07-29 SPEC STRICK) — Bouton systématiquement visible
            grâce aux fallbacks universels (≥ 2 suggestions garanties). Label
            texte AJOUTÉ à côté de l'icône pour éviter tout ambiguïté sur
            l'action (Vincent : « boutons sans texte » interdit). */}
        {canRotate && (
          <div className="flex-shrink-0 flex flex-col items-center gap-1" data-testid="next-action-rotate-wrapper">
            <button
              type="button"
              onClick={() => {
                setSuggestionIdx((i) => (i + 1) % suggestions.length);
                dismissHint();
              }}
              className="inline-flex items-center gap-1.5 px-3 py-2 rounded-full bg-[#002FA7] hover:bg-[#001F7A] text-white border border-[#002FA7] shadow-md shadow-[#002FA7]/25 hover:shadow-lg transition-all text-xs font-semibold"
              data-testid="next-action-rotate-btn"
              title={lang === "en" ? "Next suggestion" : "Prochaine suggestion"}
              aria-label={lang === "en" ? "Next suggestion" : "Prochaine suggestion"}
            >
              <ArrowClockwise size={16} weight="bold" />
              <span>{lang === "en" ? "Next" : "Suivante"}</span>
            </button>
            <span
              className="text-[10px] font-mono tabular-nums text-[#002FA7]/80 dark:text-[#38BDF8]/80 tracking-tight font-semibold"
              data-testid="next-action-rotate-counter"
              aria-label={lang === "en" ? `Suggestion ${idx + 1} of ${suggestions.length}` : `Suggestion ${idx + 1} sur ${suggestions.length}`}
            >
              {idx + 1}/{suggestions.length}
            </span>
            {/* Vincent B598.b — Hint textuel 1× par session */}
            {showHint && (
              <span
                className="mt-0.5 text-[10px] leading-tight text-[#002FA7]/90 dark:text-[#38BDF8]/90 max-w-[110px] text-center animate-pulse"
                data-testid="next-action-rotate-hint"
              >
                {lang === "en" ? "Click for other suggestions" : "Clique pour d'autres suggestions"}
              </span>
            )}
          </div>
        )}
        <div className="flex items-start sm:items-center gap-3 flex-1 min-w-0">
          <div className="w-11 h-11 sm:w-12 sm:h-12 rounded-xl bg-[#002FA7] text-white flex items-center justify-center flex-shrink-0 shadow-lg shadow-[#002FA7]/20">
            <Icon size={20} weight="fill" />
          </div>
          <div className="min-w-0">
            <p className="text-[11px] uppercase tracking-widest font-semibold text-[#002FA7]/70 dark:text-[#38BDF8]/80" data-testid="next-action-eyebrow">
              {t("next_action_title")}
            </p>
            <h2 className="text-base sm:text-lg font-bold font-['Outfit'] text-foreground mt-0.5 truncate" data-testid={`${testId}-lead`}>
              {lead}
            </h2>
            <p className="text-xs sm:text-sm text-muted-foreground mt-0.5 line-clamp-2">{sub}</p>
          </div>
        </div>
        <Link
          to={to}
          className="inline-flex items-center justify-center gap-2 px-4 py-2.5 rounded-xl bg-[#002FA7] hover:bg-[#001F7A] text-white text-sm font-semibold shadow-md shadow-[#002FA7]/25 transition-all hover:shadow-lg flex-shrink-0"
          data-testid={`${testId}-btn`}
        >
          {cta}
          <ArrowRight size={16} weight="bold" />
        </Link>
      </div>
    </div>
  );
}

function StatCard({ icon: Icon, label, value, sublabel }) {
  return (
    <div className="bg-card border border-border rounded-xl p-5" data-testid={`stat-card-${label.toLowerCase().replace(/\s+/g, '-')}`}>
      <div className="w-10 h-10 bg-secondary rounded-lg flex items-center justify-center">
        <Icon size={20} className="text-muted-foreground" />
      </div>
      <div className="mt-3">
        <div className="text-2xl font-semibold font-['Outfit']">{value}</div>
        <div className="text-sm text-muted-foreground mt-0.5">{label}</div>
        {sublabel && <div className="text-xs text-muted-foreground mt-0.5">{sublabel}</div>}
      </div>
    </div>
  );
}
